import { useEffect, useRef, useState } from "react";
import { BusyLabel } from "@/components/loading";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, API_BASE } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";
import { ArrowLeft, ArrowRight, ShieldAlert, CheckCircle2, XCircle, Camera } from "lucide-react";

// Convert HEIC/HEIF from iPhones to JPEG before upload so the preview works everywhere.
async function normalizeImage(file) {
  const name = (file.name || "").toLowerCase();
  const isHeic = name.endsWith(".heic") || name.endsWith(".heif") || file.type === "image/heic" || file.type === "image/heif";
  if (!isHeic) return file;
  try {
    const mod = await import("heic-to");
    const converter = mod.heicTo || mod.default || mod;
    const jpegBlob = await converter({ blob: file, type: "image/jpeg", quality: 0.85 });
    return new File([jpegBlob], name.replace(/\.(heic|heif)$/, ".jpg"), { type: "image/jpeg" });
  } catch (e) {
    return file; // backend will still try to convert
  }
}

function PhotoInput({ id, label, hint, file, onChange, testid }) {
  const inputRef = useRef(null);
  const [preview, setPreview] = useState(null);
  useEffect(() => {
    if (!file) { setPreview(null); return; }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);
  const pick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const norm = await normalizeImage(f);
    onChange(norm);
  };
  const clear = () => { onChange(null); if (inputRef.current) inputRef.current.value = ""; };
  return (
    <div className="bg-[#0F0F0F] border border-[#262626] rounded-md p-4">
      <Label className="text-xs uppercase tracking-wider text-neutral-400 block mb-1">{label}</Label>
      {hint && <p className="text-xs text-neutral-500 mb-3">{hint}</p>}
      {preview ? (
        <div className="space-y-2">
          <img src={preview} alt={label} className="w-full max-h-64 object-contain bg-black rounded" />
          <button type="button" onClick={clear} data-testid={`${testid}-clear`} className="text-xs text-red-400 hover:text-red-300 inline-flex items-center gap-1">
            <XCircle size={14} /> Quitar y volver a tomar
          </button>
        </div>
      ) : (
        <label htmlFor={id} className="flex flex-col items-center justify-center gap-2 p-6 border-2 border-dashed border-[#262626] rounded cursor-pointer hover:border-[#EF4444] transition">
          <Camera size={28} className="text-neutral-500" />
          <span className="text-sm text-neutral-400">Toca para tomar/subir foto</span>
        </label>
      )}
      <input
        ref={inputRef}
        id={id}
        data-testid={testid}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={pick}
      />
    </div>
  );
}

export default function ClaimAccount() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const revokeToken = params.get("revoke_token") || "";
  const [step, setStep] = useState(1); // 1 identifier, 2 photos, 3 contact/submit, 4 done
  const [identifier, setIdentifier] = useState("");
  const [claimToken, setClaimToken] = useState("");
  const [emailHint, setEmailHint] = useState("");
  const [fromNotMe, setFromNotMe] = useState(false);
  const [bootstrapping, setBootstrapping] = useState(false);
  const [bootstrapError, setBootstrapError] = useState("");
  const [selfieFile, setSelfieFile] = useState(null);
  const [cedulaFront, setCedulaFront] = useState(null);
  const [cedulaBack, setCedulaBack] = useState(null);
  const [contactEmail, setContactEmail] = useState("");
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [claimId, setClaimId] = useState("");
  const bootstrapAttempted = useRef(false);

  // If arriving from the "no fui yo" email link, auto-bootstrap: lock the account
  // + create claim_session server-side, skip step 1.
  useEffect(() => {
    if (!revokeToken) return;
    if (bootstrapAttempted.current) return;
    bootstrapAttempted.current = true;
    (async () => {
      setBootstrapping(true);
      try { localStorage.removeItem("hittcg_token"); } catch (e) { /* noop */ }
      try {
        const r = await api.post("/auth/claim-account/init-from-lock", { revoke_token: revokeToken });
        setClaimToken(r.data.claim_token);
        setEmailHint(r.data.original_email_hint || "");
        setFromNotMe(true);
        setStep(2);
      } catch (err) {
        setBootstrapError(err.response?.data?.detail || "Enlace inválido o expirado");
      } finally { setBootstrapping(false); }
    })();
  }, [revokeToken]);

  const submitIdentifier = async (e) => {
    e.preventDefault();
    if (!identifier.trim()) { toast.error("Ingresa email o cédula"); return; }
    setLoading(true);
    try {
      const r = await api.post("/auth/claim-account/init", { identifier: identifier.trim() });
      setClaimToken(r.data.claim_token);
      setStep(2);
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo iniciar el reclamo");
    } finally { setLoading(false); }
  };

  const uploadOne = async (file, kind) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("kind", kind);
    fd.append("claim_token", claimToken);
    // Use fetch directly to keep this endpoint anonymous (no Bearer)
    const res = await fetch(`${API_BASE}/auth/claim-account/upload`, { method: "POST", body: fd });
    if (!res.ok) {
      const j = await res.json().catch(() => ({}));
      throw new Error(j.detail || "Error al subir foto");
    }
    return (await res.json()).path;
  };

  const submitAll = async () => {
    if (!selfieFile || !cedulaFront || !cedulaBack) { toast.error("Sube las 3 fotos"); return; }
    if (!contactEmail || !contactEmail.includes("@")) { toast.error("Ingresa un correo de contacto válido"); return; }
    if (!acceptTerms) { toast.error("Debes aceptar la declaración de veracidad"); return; }
    setLoading(true);
    try {
      const [selfiePath, frontPath, backPath] = await Promise.all([
        uploadOne(selfieFile, "claim_selfie"),
        uploadOne(cedulaFront, "claim_cedula_front"),
        uploadOne(cedulaBack, "claim_cedula_back"),
      ]);
      const r = await api.post("/auth/claim-account/submit", {
        claim_token: claimToken,
        contact_email: contactEmail.trim().toLowerCase(),
        new_selfie_path: selfiePath,
        new_cedula_front_path: frontPath,
        new_cedula_back_path: backPath,
      });
      setClaimId(r.data.claim_id);
      setStep(4);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || "No se pudo enviar el reclamo");
    } finally { setLoading(false); }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-12">
      <div className="flex justify-center mb-8"><Logo /></div>

      <div className="bg-[#111] border border-[#262626] rounded-md p-6 md:p-8">
        <button
          type="button"
          onClick={() => step === 1 ? nav("/login") : setStep((s) => Math.max(fromNotMe ? 2 : 1, s - 1))}
          data-testid="claim-back"
          className="inline-flex items-center gap-1 text-xs text-neutral-400 hover:text-white mb-4"
        >
          <ArrowLeft size={14} /> Volver
        </button>

        <div className="flex items-center gap-3 mb-2">
          <ShieldAlert size={22} className="text-[#EF4444]" />
          <h1 className="font-display text-3xl">RECUPERAR CUENTA</h1>
        </div>
        <p className="text-neutral-400 text-sm mb-6">
          ¿Perdiste acceso al correo o crees que alguien tomó tu cuenta? Un administrador verificará tu identidad comparando tus documentos originales con fotos nuevas.
        </p>

        {fromNotMe && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-md p-4 mb-6 text-sm text-red-200" data-testid="claim-notme-banner">
            <div className="flex items-start gap-2">
              <ShieldAlert size={18} className="text-[#EF4444] mt-0.5 flex-shrink-0" />
              <div>
                <b>Bloqueamos tu cuenta por seguridad.</b> Reportaste &quot;no fui yo&quot; desde tu correo{emailHint ? <> (<span className="font-mono">{emailHint}</span>)</> : null}.
                Ahora sube fotos <b>nuevas</b> para que el admin verifique tu identidad. No podrás iniciar sesión hasta que apruebe el reclamo.
              </div>
            </div>
          </div>
        )}

        {bootstrapping && (
          <div className="text-center text-neutral-400 py-8" data-testid="claim-bootstrapping">Bloqueando cuenta y preparando reclamo…</div>
        )}

        {bootstrapError && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-md p-4 text-red-300 text-sm mb-4" data-testid="claim-bootstrap-error">
            <div className="flex items-start gap-2">
              <XCircle size={18} className="mt-0.5 flex-shrink-0" />
              <div>
                <b>No pudimos abrir el reclamo desde el enlace.</b> {bootstrapError}. Puedes empezar el proceso manualmente abajo.
              </div>
            </div>
            <button
              type="button"
              onClick={() => setBootstrapError("")}
              className="mt-3 text-xs text-neutral-300 hover:text-white underline"
              data-testid="claim-manual-restart"
            >
              Empezar reclamo manual
            </button>
          </div>
        )}

        {/* Progress */}
        {!bootstrapping && !bootstrapError && (
        <div className="flex items-center gap-2 mb-6 text-xs" data-testid="claim-steps">
          {[1, 2, 3].map((n, i) => (
            <div key={n} className="flex items-center gap-2 flex-1">
              <div className={`h-2 flex-1 rounded ${step >= n ? "bg-[#EF4444]" : "bg-[#262626]"}`} />
              {i < 2 && <span className="text-neutral-600">·</span>}
            </div>
          ))}
        </div>)}

        {!bootstrapping && !bootstrapError && step === 1 && !fromNotMe && (
          <form onSubmit={submitIdentifier} className="space-y-4">
            <h2 className="font-display text-xl">Paso 1 · Identifica la cuenta</h2>
            <div>
              <Label className="text-neutral-400 mb-2 block text-xs uppercase tracking-wider">Email original o cédula</Label>
              <Input
                data-testid="claim-identifier"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                placeholder="tucorreo@ejemplo.com o 0912345678"
                required
                className="bg-[#171717] border-[#262626] text-white"
              />
              <p className="text-xs text-neutral-500 mt-2">
                No mostraremos las fotos originales que subiste al registrarte. Solo el admin las verá para comparar.
              </p>
            </div>
            <Button data-testid="claim-init" type="submit" disabled={loading} className="w-full bg-[#EF4444] hover:bg-[#DC2626]">
              {loading ? "Iniciando…" : <>Continuar <ArrowRight size={16} className="ml-2" /></>}
            </Button>
          </form>
        )}

        {!bootstrapping && !bootstrapError && step === 2 && (
          <div className="space-y-4">
            <h2 className="font-display text-xl">Paso 2 · Toma fotos NUEVAS ahora</h2>
            <p className="text-xs text-neutral-400 bg-amber-500/10 border border-amber-500/30 rounded p-3">
              <b>Importante:</b> las fotos deben ser tomadas <b>en este momento</b>, no valen fotos guardadas. El admin verifica cara + número de cédula.
            </p>

            <PhotoInput
              id="claim-selfie-input"
              testid="claim-selfie"
              label="Selfie sosteniendo tu cédula"
              hint="Tu cara y el frente de la cédula deben verse claros en la misma foto."
              file={selfieFile}
              onChange={setSelfieFile}
            />
            <PhotoInput
              id="claim-front-input"
              testid="claim-cedula-front"
              label="Cédula — frente"
              hint="La foto oficial y el número deben leerse."
              file={cedulaFront}
              onChange={setCedulaFront}
            />
            <PhotoInput
              id="claim-back-input"
              testid="claim-cedula-back"
              label="Cédula — reverso"
              hint="Se debe ver la parte trasera completa."
              file={cedulaBack}
              onChange={setCedulaBack}
            />

            <Button
              data-testid="claim-photos-next"
              onClick={() => {
                if (!selfieFile || !cedulaFront || !cedulaBack) { toast.error("Sube las 3 fotos"); return; }
                setStep(3);
              }}
              className="w-full bg-[#EF4444] hover:bg-[#DC2626]"
            >
              Continuar <ArrowRight size={16} className="ml-2" />
            </Button>
          </div>
        )}

        {!bootstrapping && !bootstrapError && step === 3 && (
          <div className="space-y-4">
            <h2 className="font-display text-xl">Paso 3 · Correo de contacto</h2>
            <p className="text-xs text-neutral-400">
              Al aprobar tu identidad, el admin enviará un código a este correo. <b>Puede ser distinto</b> al original si perdiste acceso.
            </p>
            <div>
              <Label className="text-neutral-400 mb-2 block text-xs uppercase tracking-wider">Nuevo correo de contacto</Label>
              <Input
                data-testid="claim-contact-email"
                type="email"
                value={contactEmail}
                onChange={(e) => setContactEmail(e.target.value)}
                placeholder="micorreoactual@ejemplo.com"
                required
                className="bg-[#171717] border-[#262626] text-white"
              />
            </div>
            <label className="flex items-start gap-2 text-xs text-neutral-400 cursor-pointer">
              <input
                data-testid="claim-accept-terms"
                type="checkbox"
                checked={acceptTerms}
                onChange={(e) => setAcceptTerms(e.target.checked)}
                className="mt-0.5"
              />
              <span>
                Declaro bajo juramento que soy el titular de esta cuenta y que las fotos son mías y actuales. Entiendo que un reclamo falso puede ser reportado a las autoridades.
              </span>
            </label>
            <Button
              data-testid="claim-submit"
              onClick={submitAll}
              disabled={loading}
              className="w-full bg-[#EF4444] hover:bg-[#DC2626]"
            >
              <BusyLabel busy={loading} text="Enviando…">Enviar reclamo</BusyLabel>
            </Button>
          </div>
        )}

        {!bootstrapping && !bootstrapError && step === 4 && (
          <div className="text-center py-6" data-testid="claim-success">
            <CheckCircle2 size={48} className="text-emerald-400 mx-auto mb-4" />
            <h2 className="font-display text-2xl mb-2">Reclamo enviado</h2>
            <p className="text-sm text-neutral-400 mb-6 max-w-md mx-auto">
              Un administrador comparará tus fotos originales con las nuevas y te contactará a <span className="text-white">{contactEmail}</span> en 24-72 horas.
              <br /><br />
              Mientras tanto, la cuenta permanece bloqueada por seguridad.
            </p>
            <div className="text-xs font-mono text-neutral-500">Referencia: {claimId.slice(0, 8)}</div>
            <div className="mt-6">
              <Link to="/" className="text-[#EF4444] hover:underline text-sm">Volver al inicio</Link>
            </div>
          </div>
        )}

        <div className="mt-8 pt-6 border-t border-[#262626] text-sm text-neutral-500 text-center">
          <Link to="/login" className="hover:text-white">Volver al inicio de sesión</Link>
        </div>
      </div>
    </div>
  );
}
